// infrastructure/api/auditApi.js

import { callFirebaseFunction } from 'boot/firebase';
import { deleteField } from 'firebase/firestore';
import { useTime } from 'src/composables/time';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionActivityAuditLearningsDB from 'src/fb/sessionActivityAuditLearnings-db';
import SessionNoteTemplatesDB from 'src/fb/sessionNoteTemplates-db';
import { isAIUnavailableError } from 'src/utils/aiErrors';

import { AUDIT_TRIGGERS } from '../../domain/constants/auditTriggers';
import { AIUnavailableError } from '../../domain/errors/AIUnavailableError';

import { AUDIT_CALL_TIMEOUT_MS } from './auditCallTimeout';

/** @typedef {import('../../domain/entities/Audit.types').AuditFeedbackStance} AuditFeedbackStance */
/** @typedef {import('../../domain/entities/Audit.types').AuditVerdict} AuditVerdict */
/** @typedef {import('../../domain/entities/AuditLearning.types').FeedbackLearningOutcome} FeedbackLearningOutcome */
/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearningDbRecord} AuditLearningDbRecord */
/** @typedef {import('../../domain/entities/AuditLearning.types').NoteTemplateOption} NoteTemplateOption */
/** @typedef {import('../../domain/constants/auditTriggers').AuditTrigger} AuditTrigger */

/**
 * @typedef {Object} AuditSessionActivityResult
 * @property {AuditVerdict|null} verdict
 * @property {string|null} sessionActivityAuditId
 * @property {string|null} notesHash
 * @property {number|null} auditRunCount - Runs on this note across every client, not just this one.
 */

async function callAuditor(...args) {
  try {
    return await callFirebaseFunction(...args);
  } catch (err) {
    throw isAIUnavailableError(err) ? new AIUnavailableError({ cause: err }) : err;
  }
} // callAuditor()

/**
 * A backend that predates the check-out surface answers with a bare verdict string. Both answers land
 * on one shape here, or a caller reading `.verdict` off a string silently gets undefined.
 *
 * @returns {AuditSessionActivityResult}
 */
function toAuditResult(data, trigger) {
  if (trigger === AUDIT_TRIGGERS.CHECKOUT && (!data || typeof data !== 'object')) {
    const error = new Error('This backend cannot review a note at check-out');
    error.code = 'unavailable';

    throw error;
  }

  const result = typeof data === 'string' ? { verdict: data } : (data ?? {});

  return {
    verdict: result.verdict ?? null,
    sessionActivityAuditId: result.sessionActivityAuditId ?? null,
    notesHash: result.notesHash ?? null,
    auditRunCount: result.auditRunCount ?? null,
    flagResolution: result.flagResolution ?? null,
  };
} // toAuditResult()

/**
 * Audit a single session activity's note.
 *
 * @param {{
 *   clinicId: string,
 *   sessionActivityId: string,
 *   focusAreas: string[],
 *   strictness: string,
 *   trigger?: AuditTrigger,
 *   notes?: string,
 *   isRerun?: boolean,
 * }} params - `notes` is required for the check-out trigger, where the note is not saved yet.
 *   `isRerun` tells the audit trail whether the user asked for a check-out run.
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional signal to cancel the audit request.
 * @returns {Promise<AuditSessionActivityResult>}
 */
// eslint-disable-next-line import/prefer-default-export
export async function auditSessionActivity(params, options = {}) {
  const { signal } = options;
  const { currentIANATimezone } = useTime();

  // If already aborted, fail fast without making the call.
  if (signal?.aborted) {
    throw new DOMException('Audit cancelled', 'AbortError');
  }

  const callPromise = callAuditor('auditSessionActivity', {
    clinicId: params.clinicId,
    sessionActivityId: params.sessionActivityId,
    focusAreas: params.focusAreas,
    strictness: params.strictness,
    timezone: currentIANATimezone.value || Intl.DateTimeFormat().resolvedOptions().timeZone,
    clientTimeoutMs: AUDIT_CALL_TIMEOUT_MS,
    ...(params.trigger ? { trigger: params.trigger } : {}),
    ...(params.notes === undefined ? {} : { notes: params.notes }),
    ...(params.isRerun === undefined ? {} : { isRerun: params.isRerun }),
  }, { timeout: AUDIT_CALL_TIMEOUT_MS });
  // Race the call against the abort signal.
  const { data } = await Promise.race([
    callPromise,
    new Promise((_, reject) => {
      signal?.addEventListener('abort', () => {
        reject(new DOMException('Audit cancelled', 'AbortError'));
      }, { once: true });
    }),
  ]);

  return toAuditResult(data, params.trigger);
} // auditSessionActivity()

/**
 * Submits feedback for an audit result, including the user's stance, comment, and corrected verdict.
 *
 * @param {{
 *   clinicId: string,
 *   sessionActivityAuditId: string,
 *   stance: AuditFeedbackStance,
 *   comment?: string|null,
 *   correctedVerdict?: AuditVerdict|null,
 * }} params
 * @returns {Promise<FeedbackLearningOutcome>}
 */
export async function submitAuditFeedback({
  clinicId,
  sessionActivityAuditId,
  stance,
  comment = null,
  correctedVerdict = null,
}) {
  const { data } = await callAuditor('auditSessionActivityFeedback', {
    clinicId,
    sessionActivityAuditId,
    stance,
    comment,
    correctedVerdict,
  });

  if (!data.success) {
    throw new Error('Failed to submit audit feedback');
  }

  return {
    learningDecision: data.learningDecision,
    proposedLearning: data.proposedLearning ? {
      content: data.proposedLearning.content,
      headline: data.proposedLearning.headline,
      reasoning: data.proposedLearning.reasoning,
    } : null,
    updatedLearning: data.updatedLearning ? {
      content: data.updatedLearning.content,
      headline: data.updatedLearning.headline,
      reasoning: data.updatedLearning.reasoning,
    } : null,
    matchedLearningId: data.matchedLearningId ?? null,
    matchedLearning: data.matchedLearning ? {
      id: data.matchedLearning.id,
      content: data.matchedLearning.content,
    } : null,
    deletionReasoning: data.deletionReasoning ?? null,
    skipReason: data.skipReason ?? null,
  };
} // submitAuditFeedback()

/**
 * Commits a learning to the system, making it available for future audits and model training.
 *
 * `context` is the thumbs-down flow's payload; `sessionNoteTemplateIds` is the moderation page's. Sending
 * the latter is what marks the write as manually authored, so the two are never sent together.
 *
 * @param {{
 *   clinicId: string,
 *   learning: string,
 *   context?: {
 *     sessionActivityType: string,
 *     sessionNoteTemplateId?: string,
 *   },
 *   sessionNoteTemplateIds?: string[],
 *   saveAsDraft?: boolean,
 *   parentId?: string|null,
 *   source?: string|null,
 *   sessionActivityAuditId?: string|null,
 * }} params
 * @returns {Promise<{ success: boolean, id: string }>}
 */
export async function auditSessionActivityCommitLearning({
  clinicId,
  learning,
  context = null,
  sessionNoteTemplateIds = undefined,
  saveAsDraft = false,
  parentId = null,
  source = null,
  sessionActivityAuditId = null,
}) {
  const { data } = await callAuditor('auditSessionActivityCommitLearning', {
    clinicId,
    learning,
    context,
    sessionNoteTemplateIds,
    saveAsDraft,
    parentId,
    source,
    sessionActivityAuditId,
  });
  return data;
}

/**
 * Updates the existing learning in the system.
 *
 * A verified save of a draft revision is where the backend promotes it over the active learning it
 * replaces and deletes that one, in a single write — hence `promoted` in the result.
 *
 * @param {{
 *   clinicId: string,
 *   learningId: string,
 *   learning: string,
 *   sessionNoteTemplateIds?: string[],
 *   saveAsDraft?: boolean,
 *   source?: string|null,
 * }} params
 * @returns {Promise<{ success: boolean, promoted: boolean }>}
 */
export async function auditSessionActivityUpdateLearning({
  clinicId,
  learningId,
  learning,
  sessionNoteTemplateIds = undefined,
  saveAsDraft = false,
  source = null,
}) {
  const { data } = await callAuditor('auditSessionActivityUpdateLearning', {
    clinicId,
    learningId,
    learning,
    sessionNoteTemplateIds,
    saveAsDraft,
    source,
  });
  return data;
}

/**
 * Removes outdated learning from the system.
 * @param {{
 *   clinicId: string,
 *   learningId: string,
 * }} params
 * @returns {Promise<{ success: boolean }>}
 */
export async function auditSessionActivityDeleteLearning({
  clinicId,
  learningId,
}) {
  const { data } = await callAuditor('auditSessionActivityDeleteLearning', {
    clinicId,
    learningId,
  });
  return data;
}

/**
 * Clears the audit result from a session activity. This does not delete the
 * associated SessionActivityAudit document, but simply removes the audit result data
 * from the session activity.
 *
 * @param {string} sessionActivityId - The ID of the session activity.
 * @returns {Promise<void>}
 */
export async function removeAuditFromSessionActivity(sessionActivityId) {
  const sessionActivitiesDb = new SessionActivitiesDB();
  await sessionActivitiesDb.set({ audit: deleteField() }, sessionActivityId);
} // removeAuditFromSessionActivity()

/**
 * Returns all audit learnings for the specified clinic.
 *
 * @param {string} clinicId
 * @returns {Promise<AuditLearningDbRecord[]>}
 */
export async function getAuditLearnings(clinicId) {
  const sessionActivityAuditLearningsDb = new SessionActivityAuditLearningsDB();

  return sessionActivityAuditLearningsDb.readAll([
    ['clinic', '==', clinicId],
  ]);
} // getAuditLearnings()

/**
 * Returns the clinic's session note templates, as the learning scope selector and the "Applies to" column
 * both need them.
 *
 * Form templates are excluded: they are not session notes and are never audited. The same in-memory
 * filter is used on the Note Templates settings page, since `sessionActivityTypes` cannot be negated in a
 * Firestore query.
 *
 * @param {string} clinicId
 * @returns {Promise<NoteTemplateOption[]>}
 */
export async function getClinicSessionNoteTemplates(clinicId) {
  if (!clinicId) {
    return [];
  }

  const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();

  const sessionNoteTemplates = await sessionNoteTemplatesDb.readAll([
    ['clinic', '==', clinicId],
  ]);

  return sessionNoteTemplates
    .filter((template) => !template.sessionActivityTypes?.includes('form'))
    .map(({ id, noteTemplateName }) => ({ id, noteTemplateName }));
} // getClinicSessionNoteTemplates()
